import Component from '@glimmer/component';
import { assert } from '@ember/debug';
import { set } from '@ember/object';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from 'tracked-built-ins';
import { t } from 'ember-intl';
import { on } from '@ember/modifier';
import { not, or } from 'ember-truth-helpers';
import {
  HdsFormTextInputField,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { WithErrors, FlexGrid, Typography, Flex } from 'core/components';
import { boxPadding } from 'core/modifiers';

const baseKey = 'components.iam.groups.form';

/**
 *
 * `IamGroupsForm` is the parent wrapper component for the HCP Groups Organization list page
 *
 *
 * ```
 * <Iam::Groups::Form
 *   @saveButtonText='Create group'
 *   @save={{this.save}}
 *   @isSaveDisabled={{false}}
 * />
 * ```
 *
 * @class IamGroupsForm
 *
 */
export default class IamGroupsForm extends Component {
  /**
   * Button text to use for "save" action.
   * @argument saveButtonText
   * @type {String}
   * @required
   */

  /**
   * Passed in async/generator Ember concurrency task with a perform callback
   * function to invoke upon form submission
   * NOTE: This form component just collects and validates input, doesn't call API
   * @argument save
   * @type {Function}
   * @required
   */

  /**
   * Optionally disables the form submit button that would invoke the save action
   * @argument isSaveDisabled
   * @type {Boolean}
   */
  @service api;
  @service intl;
  @service flashMessages;
  @service router;
  @service userContext;

  constructor() {
    super(...arguments);

    assert(
      '<Iam::Groups::Form> argument @saveButtonText is a required non-empty string',
      typeof this.args.saveButtonText === 'string' &&
        this.args.saveButtonText.length >= 1
    );

    assert(
      '<Iam::Groups::Form> argument @save is a required Ember concurrency task with a .perform callback function',
      typeof this.args.save?.perform === 'function'
    );
  }

  validate = task(async (evt) => {
    evt.preventDefault();

    if (!this.validateForm()) return;
    // refer to callback save
    return await this.args.save.perform(this.form);
  });

  // May only contain letters, numbers, underscores, periods, hypens.
  nameInvalidCharacters = /[^a-zA-Z0-9\s_\-.]/;

  setName = (e) => {
    this.form.fields.name = e.target.value;
    this.validateForm({ name: true });
  };

  setDescription = (e) => {
    this.form.fields.description = e.target.value;
    this.validateForm({ description: true });
  };

  @tracked form = {
    fields: this.args.groupFormFields || {
      name: '',
      description: '',
    },
    validationErrors: {
      name: [],
      description: [],
    },
    fieldValidators: {
      name: [
        {
          message: this.intl.t(`${baseKey}.errors.required`),
          isValid: (v) => v && v !== '',
          subValidators: [
            {
              message: this.intl.t(
                `${baseKey}.create-group.errors.name.must-only-contain-valid-characters`
              ),
              isValid: (v) => !this.nameInvalidCharacters.test(v),
            },
            {
              message: this.intl.t(
                `${baseKey}.create-group.errors.name.must-be-between-specific-length`
              ),
              isValid: (v) => v.length >= 1 && v.length <= 128,
            },
          ],
        },
      ],
      description: [
        {
          message: this.intl.t(
            `${baseKey}.create-group.errors.description.cannot-exceed-specific-length`
          ),
          isValid: (v) => v.length <= 300,
        },
      ],
    },
  };

  validateForm(
    { name = false, description = false } = { name: true, description: true }
  ) {
    if (name) {
      let nameErrors = this.runValidators(
        this.form.fieldValidators.name,
        this.form.fields.name
      );
      set(
        this.form,
        'validationErrors.name',
        nameErrors.map((v) => v.message)
      );
    }
    if (description) {
      let descriptionErrors = this.runValidators(
        this.form.fieldValidators.description,
        this.form.fields.description
      );
      set(
        this.form,
        'validationErrors.description',
        descriptionErrors.map((v) => v.message)
      );
    }

    return !Object.keys(this.form.validationErrors).find(
      (k) => this.form.validationErrors[k].length
    );
  }

  runValidators(validators, value) {
    let failedValidators = validators.flatMap((v) => {
      if (!v.isValid(value)) {
        return v;
      }

      if (v.subValidators) {
        return this.runValidators(v.subValidators, value);
      }

      return [];
    });

    return failedValidators;
  }
  <template>
    <WithErrors @error={{this.validate.last.error}} as |FormError FieldError|>
      <FormError />
      <div {{boxPadding (if this.validate.last.error "md 0 0 0" "0")}}>
        <FlexGrid as |G|>
          <G.Item @xs="12" @sm="8" @md="6" @lg="5">
            <Typography
              @variant="h2"
              class="hds-typography-display-300"
              {{boxPadding "0 0 xl 0"}}
            >
              {{t "components.iam.groups.form.create-group.label.title"}}
            </Typography>
            <form
              data-test-group-form
              novalidate
              class="hcp-hds-form"
              {{on "submit" this.validate.perform}}
            >
              <Flex @direction="column" @gap="xl">
                <section>
                  <Flex @direction="column" @gap="md">
                    <div>
                      <FieldError
                        @field="name"
                        @hdsDelegatedError={{true}}
                        as |err|
                      >
                        <HdsFormTextInputField
                          @extraAriaDescribedBy="name-error"
                          @id="name"
                          @isRequired={{not @isEditing}}
                          @isInvalid={{or
                            err
                            this.form.validationErrors.name.length
                          }}
                          @value={{this.form.fields.name}}
                          name="name"
                          data-test-group-name
                          {{on "input" this.setName}}
                          {{on "blur" this.setName}}
                          as |F|
                        >
                          <F.Label>
                            {{t
                              "components.iam.groups.form.create-group.label.name"
                            }}
                          </F.Label>
                          <F.HelperText>
                            {{#unless @isEditing}}
                              {{t
                                "components.iam.groups.form.create-group.helper-text.name"
                              }}
                            {{/unless}}
                          </F.HelperText>
                          {{#if err}}
                            <F.Error>
                              {{err.description}}
                            </F.Error>
                          {{else if this.form.validationErrors.name.length}}
                            <F.Error data-test-validation-name-error as |E|>
                              {{#each this.form.validationErrors.name as |m|}}
                                <E.Message>
                                  {{m}}
                                </E.Message>
                              {{/each}}
                            </F.Error>
                          {{/if}}
                        </HdsFormTextInputField>
                      </FieldError>
                    </div>
                    <div>
                      <FieldError
                        @field="description"
                        @hdsDelegatedError={{true}}
                      >
                        <HdsFormTextInputField
                          @extraAriaDescribedBy="description-error"
                          @id="description"
                          @value={{this.form.fields.description}}
                          name="description"
                          data-test-group-description
                          {{on "input" this.setDescription}}
                          {{on "blur" this.setDescription}}
                          as |F|
                        >
                          <F.Label>
                            {{t
                              "components.iam.groups.form.create-group.label.description"
                            }}
                          </F.Label>
                          <F.HelperText>
                            {{t
                              "components.iam.groups.form.create-group.helper-text.description"
                            }}
                          </F.HelperText>
                          {{#if this.form.validationErrors.description.length}}
                            <F.Error
                              data-test-validation-description-error
                              as |E|
                            >
                              {{#each
                                this.form.validationErrors.description
                                as |m|
                              }}
                                <E.Message>
                                  {{m}}
                                </E.Message>
                              {{/each}}
                            </F.Error>
                          {{/if}}
                        </HdsFormTextInputField>
                      </FieldError>
                    </div>
                  </Flex>
                </section>

                <footer>
                  <HdsButtonSet>
                    <HdsButton
                      data-test-submit
                      @text={{@saveButtonText}}
                      type="submit"
                      disabled={{or @isSaveDisabled this.validate.isRunning}}
                    />
                    <HdsButton
                      @color="secondary"
                      @route="cloud.access-control.groups.list"
                      @text={{t "components.form.cancel"}}
                      data-test-cancel
                    />
                  </HdsButtonSet>
                </footer>
              </Flex>
            </form>
          </G.Item>
        </FlexGrid>
      </div>
    </WithErrors>
  </template>
}
